import { DOCUMENT } from '@angular/common';
import {
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  animationFrameScheduler,
  asapScheduler,
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  fromEvent,
  map,
  merge,
  mergeMap,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Point, PointType } from '../../helpers/coords/point';
import { Rect } from '../../helpers/coords/rect';
import { MovingUtils } from '../../utils/moving.utils';
import { RxUtils } from '../../utils/rx.utils';
import { CardShapeType } from '../card/card.component';
import { OverlayComponent } from '../overlay/overlay.component';
import { TranslationService } from '../../services/translation.service';

export type VerticalAlignment = 'top' | 'bottom' | 'center';
export type HorizontalAlignment = 'left' | 'right' | 'center';

@Directive({
  selector: 'ng-template[modalContent]',
})
export class ContentTemplate {
  constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
  selector: 'app-modal',
  templateUrl: 'modal.component.html',
  styleUrls: ['modal.component.scss'],
})
export class ModalComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild(ContentTemplate, { static: false })
  contentTpl: ContentTemplate;

  @ViewChild(OverlayComponent, { static: false })
  overlayComp: OverlayComponent;
  @ViewChild('modalEl', { static: false })
  modalElRef: ElementRef<HTMLDivElement>;
  @ViewChild('modalHeaderEl', { static: false })
  modalHeaderElRef: ElementRef<HTMLDivElement>;
  @ViewChild('modalBodyEl', { static: false })
  modalBodyElRef: ElementRef<HTMLDivElement>;

  @Input()
  visible: boolean;
  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Output()
  onShowing = new EventEmitter<any>();
  @Output()
  onShown = new EventEmitter<any>();
  @Output()
  onHiding = new EventEmitter<any>();
  @Output()
  onHidden = new EventEmitter<any>();

  @Output()
  onContentReady = new EventEmitter<void>();

  @Input()
  minimized = false;
  @Output()
  minimizedChange = new EventEmitter<boolean>();

  @Input()
  overlayMask = true;

  @Input()
  useHeaderAsDragHandle = true;
  @Input()
  useBodyAsDragHandle: boolean;

  @Input()
  showToolbar = true;
  @Input()
  modalTitle: string;
  @Input()
  showModalTitle = true;
  @Input()
  modalTitleLocation: 'before' | 'center' = 'before';
  @Input()
  closeBtnEnabled = true;
  @Input()
  minimizeBtnEnabled = false;
  @Input()
  draggableEnabled = false;
  @Input()
  restrictDraggingToContainer = true;
  @Input()
  thresholdDragging: number = 0;
  @Input()
  scrollEnabled = true;

  @Input()
  shapeType: CardShapeType = 'rounded';

  @Input()
  width: string;
  @Input()
  height: string;
  @Input()
  maxWidth: string;
  @Input()
  maxHeight: string;

  @Input()
  modalClass: string;

  @Input()
  closeOutsideClickEnabled = true;

  @Input()
  bringToFrontOnClickEnabled = false;

  @Input()
  container: HTMLElement = this._document.body;

  @Input()
  modalVerticalAlignment: VerticalAlignment = 'center';
  @Input()
  modalHorizontalAlignment: HorizontalAlignment = 'center';

  @Input()
  containerVerticalAlignment: VerticalAlignment = 'center';
  @Input()
  containerHorizontalAlignment: HorizontalAlignment = 'center';

  private _currentInputCoords: Point;
  private _currentCoords: Point;

  private _coords: Point;
  @Input()
  posX: number;
  @Output()
  posXChange = new EventEmitter<number>();

  @Input()
  posY: number;
  @Output()
  posYChange = new EventEmitter<number>();

  @Output()
  onCoordsChanged = new EventEmitter<Point>();

  private _computedPosX: number;
  private _computedPosY: number;

  private modalRect: Rect;
  private containerRect: Rect;

  private _containerChangedSubject = new BehaviorSubject<void>(null);

  private _contentReadySubject = new ReplaySubject<void>(1);

  private _minimizeInternalChangedSubject = new BehaviorSubject<boolean>(
    !!this.minimized
  ).pipe(distinctUntilChanged()) as Subject<boolean>;

  private _restrictDraggingToContainerInternalChangedSubject =
    new BehaviorSubject<boolean>(!!this.restrictDraggingToContainer).pipe(
      distinctUntilChanged()
    ) as Subject<boolean>;

  private _alignmentFnInternalChangedSubject = new Subject<void>();
  private _coordsInternalChangedSubject = new Subject<void>();

  private modalStylesSubscriptionMap = new Map<string, Subscription>();
  private posEventsSubscription: Subscription;

  private draggingEventsSubscription: Subscription;

  private initialized = false;

  rendered = false;

  private lifecycle = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
    private zone: NgZone,
    private translationService: TranslationService
  ) {}

  get minimizeTooltip(): string {
    const translations = this.translationService.getCurrentLanguage();
    return this.minimized ? translations['expand'] : translations['collapse'];
  }

  get closeTooltip(): string {
    return this.translationService.getCurrentLanguage()['close'];
  }

  ngOnInit(): void {
    this.handlePosEvents();

    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.width) {
      this.handleModalSyle('--modal-width', this.width ?? '20rem');
    }

    if (changes.height) {
      this.handleModalSyle('--modal-height', this.height ?? 'auto');
    }

    if (changes.maxWidth) {
      this.handleModalSyle('--modal-max-width', this.maxWidth ?? '100%');
    }

    if (changes.maxHeight) {
      this.handleModalSyle('--modal-max-height', this.maxHeight ?? 'auto');
    }

    if (
      changes.draggableEnabled ||
      changes.useHeaderAsDragHandle ||
      changes.useBodyAsDragHandle
    ) {
      this.draggingEventsSubscription?.unsubscribe();
      if (this.draggableEnabled) {
        this.setupDraggingEvents();
      }
    }

    if (changes.container) {
      this.zone.runOutsideAngular(() => {
        this._containerChangedSubject.next();
      });
    }

    if (changes.minimized) {
      this.zone.runOutsideAngular(() => {
        this._minimizeInternalChangedSubject.next(!!this.minimized);
      });
    }

    if (changes.restrictDraggingToContainer) {
      this.zone.runOutsideAngular(() => {
        this._restrictDraggingToContainerInternalChangedSubject.next(
          !!this.restrictDraggingToContainer
        );
      });
    }

    if (
      !this.initialized ||
      changes.modalVerticalAlignment ||
      changes.modalHorizontalAlignment ||
      changes.containerVerticalAlignment ||
      changes.containerHorizontalAlignment
    ) {
      this.zone.runOutsideAngular(() => {
        this._alignmentFnInternalChangedSubject.next();
      });
    }

    if (!this.initialized) {
      const coords = new Point(this.posX ?? 0, this.posY ?? 0);
      if (!coords.equals(this._coords)) {
        this._coords = coords;

        if (!this._coords.equals(this._currentCoords)) {
          this.zone.runOutsideAngular(() => {
            this._currentCoords = coords.clone();
            this._coordsInternalChangedSubject.next();
          });
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onContentReadyInternal() {
    this.zone.runOutsideAngular(() => {
      this._contentReadySubject.next();
    });

    this.zone.runGuarded(() => {
      this.onContentReady.emit();
    });
  }

  onBtnCloseClick(e) {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  onBtnMinimizeToggleClick(e) {
    this.minimized = !this.minimized;
    this._minimizeInternalChangedSubject.next(!!this.minimized);
    this.minimizedChange.emit(this.minimized);
  }

  onModalFocus() {
    if (this.bringToFrontOnClickEnabled) {
      this.bringModalToFront();
    }
  }

  bringModalToFront() {
    this.overlayComp.bringToFront();
  }

  private handlePosEvents() {
    this.zone.runOutsideAngular(() => {
      this.posEventsSubscription?.unsubscribe();
      this.posEventsSubscription = this._contentReadySubject
        .pipe(first(), takeUntil(this.lifecycle))
        .subscribe(() => {
          const subscription = new Subscription();

          const modalEl = this.modalElRef.nativeElement;

          const containerObservable = this._containerChangedSubject.pipe(
            map(() => this.resolveContainer()),
            distinctUntilChanged(),
            shareReplay({
              bufferSize: 1,
              refCount: true,
            })
          );

          subscription.add(
            containerObservable
              .pipe(
                switchMap((container) => fromEvent(container, 'click')),
                takeUntil(this.lifecycle)
              )
              .subscribe((e) => {
                if (
                  !this.closeOutsideClickEnabled ||
                  !this.visible ||
                  this.overlayComp.hidden ||
                  this.overlayComp.hiding ||
                  this.overlayComp.showing ||
                  modalEl?.contains(e.target as any)
                ) {
                  return;
                }

                this.zone.runGuarded(() => {
                  this.visible = false;
                  this.visibleChange.emit(this.visible);
                });
              })
          );

          const containerAndModalResizeObservable = containerObservable.pipe(
            switchMap((container) => {
              return RxUtils.observeElementResize(container, modalEl).pipe(
                startWith([] as ResizeObserverEntry[]),
                tap((entries) => {
                  if (!entries.length) {
                    this.containerRect = Rect.fromElement(container);
                    this.modalRect = Rect.fromElement(modalEl);
                  } else {
                    let newRect: Rect;
                    for (const entry of entries) {
                      const contentRect =
                        entry.target === container ? null : entry.contentRect;
                      const borderBoxSize = entry.borderBoxSize[0];

                      newRect = new Rect(
                        contentRect?.left ?? 0,
                        contentRect?.top ?? 0,
                        borderBoxSize.inlineSize,
                        borderBoxSize.blockSize
                      );

                      switch (entry.target) {
                        case container:
                          this.containerRect = newRect;
                          break;
                        case modalEl:
                          this.modalRect = newRect;
                          break;
                      }
                    }
                  }
                })
              );
            })
          );

          subscription.add(
            merge(
              containerAndModalResizeObservable,
              this._coordsInternalChangedSubject,
              this._restrictDraggingToContainerInternalChangedSubject,
              this._alignmentFnInternalChangedSubject
            )
              .pipe(takeUntil(this.lifecycle))
              .subscribe(() => {
                this.handlePos();

                if (!this.rendered) {
                  this.zone.runGuarded(() => {
                    this.rendered = true;
                  });
                }
              })
          );

          this.posEventsSubscription = subscription;
        });
    });
  }

  private handleModalSyle(property: string, value: string) {
    this.zone.runOutsideAngular(() => {
      this.modalStylesSubscriptionMap.get(property)?.unsubscribe();
      this.modalStylesSubscriptionMap.set(
        property,
        this._contentReadySubject
          .pipe(first(), takeUntil(this.lifecycle))
          .subscribe(() => {
            this.zone.runOutsideAngular(() => {
              this.modalElRef.nativeElement.style.setProperty(property, value);
            });
          })
      );
    });
  }

  private resolveContainer() {
    return this.container ?? this._document.body;
  }

  private handlePos() {
    this._currentInputCoords = null;
    this.setPos({ x: this._currentCoords?.x, y: this._currentCoords?.y });
  }

  private setupDraggingEvents() {
    this.zone.runOutsideAngular(() => {
      let initialX = 0;
      let initialY = 0;
      this.draggingEventsSubscription = this._contentReadySubject
        .pipe(
          first(),
          mergeMap(() => {
            const dragHandleElementList: HTMLElement[] = [];

            if (this.useHeaderAsDragHandle) {
              dragHandleElementList.push(this.modalHeaderElRef.nativeElement);
            }

            if (this.useBodyAsDragHandle) {
              dragHandleElementList.push(this.modalBodyElRef.nativeElement);
            }

            if (!dragHandleElementList.length) {
              return EMPTY;
            }

            const startEventObservable = merge(
              ...dragHandleElementList.flatMap((el) => {
                return [
                  fromEvent<any>(el, 'mousedown', {
                    passive: true,
                  }),
                  fromEvent<any>(el, 'touchstart', {
                    passive: true,
                  }),
                ];
              })
            );

            const addOrRemoveClass = (addMode: boolean) => {
              const draggingCssClass = 'state-dragging';
              const fn = addMode
                ? this.renderer.addClass
                : this.renderer.removeClass;

              fn.call(
                this.renderer,
                this.modalElRef.nativeElement,
                draggingCssClass
              );

              for (const el of dragHandleElementList) {
                fn.call(this.renderer, el, draggingCssClass);
              }
            };

            return MovingUtils.observeMovingEvents({
              startEventObservable,
              onMoveStart: (e) => {
                initialX = this._currentCoords?.x ?? 0;
                initialY = this._currentCoords?.y ?? 0;

                addOrRemoveClass(true);
              },
              onMoveEnd: () => {
                addOrRemoveClass(false);
              },
            });
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe((e) => {
          let x = initialX + e.stepPoint.x;
          let y = initialY + e.stepPoint.y;

          this.setPos({ x, y });
        });
    });
  }

  private getContainerBoundary(): Rect {
    if (!this.containerRect) {
      return null;
    }

    let rect = this.containerRect.clone();
    rect.x = 0;
    rect.y = 0;
    return rect;
  }

  private setPos(point: PointType) {
    this.zone.runOutsideAngular(() => {
      let pointAux = Point.fromObject({ x: point?.x ?? 0, y: point?.y ?? 0 });
      if (pointAux.equals(this._currentInputCoords)) {
        return;
      }
      this._currentInputCoords = pointAux.clone();

      let modalRect = this.modalRect?.clone();
      if (!(modalRect?.width && modalRect?.height)) {
        return;
      }

      const boundary = this.getContainerBoundary();
      if (!(boundary?.width && boundary?.height)) {
        return;
      }

      let x = pointAux.x;
      let y = pointAux.y;

      if (this.restrictDraggingToContainer) {
        boundary.inflate(-this.thresholdDragging);

        const boundaryAux = boundary.clone();
        switch (this.containerVerticalAlignment) {
          case 'bottom':
            boundaryAux.y -= boundary.bottom;
            break;
          case 'center':
            boundaryAux.y -= boundary.centerY;
            break;
        }

        switch (this.containerHorizontalAlignment) {
          case 'right':
            boundaryAux.x -= boundary.right;
            break;
          case 'center':
            boundaryAux.x -= boundary.centerX;
            break;
        }

        switch (this.modalVerticalAlignment) {
          case 'bottom':
            boundaryAux.y += modalRect.height;
            boundaryAux.height -= modalRect.height * 2;
            break;
          case 'center':
            boundaryAux.y += Math.floor(modalRect.height / 2);
            boundaryAux.height -= modalRect.height;
            break;
          case 'top':
          default:
            boundaryAux.height -= modalRect.height;
            break;
        }

        switch (this.modalHorizontalAlignment) {
          case 'right':
            boundaryAux.x += modalRect.width;
            boundaryAux.width -= modalRect.width * 2;
            break;
          case 'center':
            boundaryAux.x += Math.floor(modalRect.width / 2);
            boundaryAux.width -= modalRect.width;
            break;
          case 'left':
          default:
            boundaryAux.width -= modalRect.width;
            break;
        }

        if (boundaryAux.isPointNearBoundaries(pointAux)) {
          x = Math.max(boundaryAux.x, Math.min(boundaryAux.right, x));
          y = Math.max(boundaryAux.y, Math.min(boundaryAux.bottom, y));
        }
      }

      let newPoint = new Point(x, y);

      // #region COMPUTED COORDS
      const computedPoint = newPoint.clone();
      switch (this.containerVerticalAlignment) {
        case 'bottom':
          computedPoint.y += boundary.height;
          break;
        case 'center':
          computedPoint.y += boundary.height * 0.5;
          break;
      }

      switch (this.containerHorizontalAlignment) {
        case 'right':
          computedPoint.x += boundary.width;
          break;
        case 'center':
          computedPoint.x += boundary.width * 0.5;
          break;
      }

      switch (this.modalVerticalAlignment) {
        case 'bottom':
          computedPoint.y -= modalRect.height;
          break;
        case 'center':
          computedPoint.y -= modalRect.height * 0.5;
          break;
      }

      switch (this.modalHorizontalAlignment) {
        case 'right':
          computedPoint.x -= modalRect.width;
          break;
        case 'center':
          computedPoint.x -= modalRect.width * 0.5;
          break;
      }
      // #endregion

      if (!newPoint.equals(this._currentCoords)) {
        this._currentCoords = newPoint;

        this.zone.runGuarded(() => {
          if (newPoint.x !== this.posX) {
            this.posX = newPoint.x;
            this._coords.x = this.posX;
            this.posXChange.emit(this.posX);
          }

          if (newPoint.y !== this.posY) {
            this.posY = newPoint.y;
            this._coords.y = this.posY;
            this.posYChange.emit(this.posY);
          }

          this.onCoordsChanged.emit(newPoint.clone());
        });
      }

      const modalEl = this.modalElRef.nativeElement;
      if (this._computedPosX !== computedPoint.x) {
        this._computedPosX = computedPoint.x;
        this.renderer.setStyle(modalEl, 'left', `${this._computedPosX}px`);
      }

      if (this._computedPosY !== computedPoint.y) {
        this._computedPosY = computedPoint.y;
        this.renderer.setStyle(modalEl, 'top', `${this._computedPosY}px`);
      }
    });
  }
}
